/**
 * Plan, step 2 — Plan my week (Monday to Friday): each day with its Outlook meetings, free time and the tasks on it;
 * beside it the week's tasks with no day yet, which must each get one. Drag a card onto a day (or use "Move to…"),
 * or let "Spread for me" propose days. A task with no estimate counts 30 minutes.
 */
import { CategoryPick } from "../components/plan/CategoryPick";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import clsx from "clsx";
import { AlertTriangle, CalendarDays, Check, ChevronLeft, ChevronRight, GripVertical, Inbox, Shuffle } from "lucide-react";
import { useState } from "react";
import { api, patch, post } from "../lib/api";
import { Empty, ErrorNote, Modal, PageHeader } from "../components/ui";
import { PlanSteps } from "../components/plan/PlanSteps";

interface WTask {
  id: string; ref: string; title: string; priority: string; dueAt: string | null; plannedDate: string | null; focusWeek: string | null; carriedFrom?: string | null;
  estimateMinutes: number | null; minutes: number; decisionNeeded: boolean; company: string | null;
  category?: { id: string; name: string; color: string } | null;
}
interface WEvent { id: string; subject: string; start: string; end: string; isAllDay: boolean; location: string | null }
interface WDay { date: string; past: boolean; isToday: boolean; events: WEvent[]; meetingMinutes: number; freeMinutes: number; taskMinutes: number; tasks: WTask[] }
interface WeekData { week: string; planWeek?: string; today: string; workday: { start: string; end: string }; calendarConnected: boolean; days: WDay[]; unplaced: WTask[]; dueThisWeek: WTask[] }
interface Proposal { itemId: string; ref: string; title: string; day: string; why: string }

const MINUTES = [15, 30, 45, 60, 90, 120, 180, 240];
const DRAG_TYPE = "application/x-one-task";
const LIST = "list";
const QUEUE = "queue";

const addDays = (d: string, n: number) => { const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };
const mondayOf = (d: string) => addDays(d, -((new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7));
const fmtDay = (d: string, o: Intl.DateTimeFormatOptions = { weekday: "short", day: "numeric", month: "short" }) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { ...o, timeZone: "UTC" });
const fmtDue = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "Europe/Malta" });
const dueDay = (iso: string) => new Date(iso).toLocaleDateString("en-CA", { timeZone: "Europe/Malta" });
const hm = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Malta" });
const dur = (m: number) => { const h = Math.floor(m / 60), r = m % 60; return h ? (r ? `${h}h ${String(r).padStart(2, "0")}` : `${h}h`) : `${r}m`; };

/** Move a task in the cached week: off wherever it is, onto a day (or the list). */
function moveLocal(d: WeekData, id: string, to: string | null): WeekData {
  const all = [...d.unplaced, ...d.dueThisWeek, ...d.days.flatMap((x) => x.tasks)];
  const t = all.find((x) => x.id === id);
  if (!t) return d;
  const moved: WTask = { ...t, plannedDate: to, focusWeek: to ? mondayOf(to) : d.week };
  const days = d.days.map((x) => {
    const tasks = [...x.tasks.filter((k) => k.id !== id), ...(x.date === to ? [moved] : [])];
    return { ...x, tasks, taskMinutes: tasks.reduce((n, k) => n + k.minutes, 0) };
  });
  return { ...d, days, unplaced: [...d.unplaced.filter((k) => k.id !== id), ...(to ? [] : [moved])], dueThisWeek: d.dueThisWeek.filter((k) => k.id !== id) };
}

function dropLocal(d: WeekData, id: string): WeekData {
  const days = d.days.map((x) => { const tasks = x.tasks.filter((k) => k.id !== id); return { ...x, tasks, taskMinutes: tasks.reduce((n, k) => n + k.minutes, 0) }; });
  return { ...d, days, unplaced: d.unplaced.filter((k) => k.id !== id), dueThisWeek: d.dueThisWeek.filter((k) => k.id !== id) };
}

function setMinutesLocal(d: WeekData, id: string, m: number): WeekData {
  const fix = (k: WTask) => (k.id === id ? { ...k, estimateMinutes: m, minutes: m } : k);
  const days = d.days.map((x) => { const tasks = x.tasks.map(fix); return { ...x, tasks, taskMinutes: tasks.reduce((n, k) => n + k.minutes, 0) }; });
  return { ...d, days, unplaced: d.unplaced.map(fix), dueThisWeek: d.dueThisWeek.map(fix) };
}

export function PlanWeekPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { week?: string };
  const key = ["plan-week", search.week ?? "current"];
  const q = useQuery({ queryKey: key, queryFn: () => api<WeekData>(`/api/plan/week${search.week ? `?week=${search.week}` : ""}`) });
  const d = q.data;
  const [error, setError] = useState<unknown>(null);
  const [over, setOver] = useState<string | null>(null);
  const [tab, setTab] = useState<string | null>(null);
  const [spread, setSpread] = useState<Proposal[] | null>(null);
  const [spreadBusy, setSpreadBusy] = useState(false);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["plan-week"] });
    void qc.invalidateQueries({ queryKey: ["plan-sort"] });
    void qc.invalidateQueries({ queryKey: ["plan-day"] });
  };

  const place = async (id: string, to: string | null) => {
    if (!d) return;
    const now = [...d.unplaced, ...d.dueThisWeek].some((k) => k.id === id) ? null : d.days.find((x) => x.tasks.some((k) => k.id === id))?.date ?? null;
    const fromDue = d.dueThisWeek.some((k) => k.id === id);
    if (now === to && !(to === null && fromDue)) return;
    setError(null);
    qc.setQueryData<WeekData>(key, (cur) => (cur ? moveLocal(cur, id, to) : cur));
    try { await post("/api/plan/week/place", { itemId: id, day: to, ...(to ? {} : { week: d.week }) }); }
    catch (e) { setError(e); }
    refresh();
  };

  /** Take the task out of the week altogether: it goes back to Sort's "To sort" queue, with no day and no week. */
  const toQueue = async (id: string) => {
    if (!d) return;
    setError(null);
    qc.setQueryData<WeekData>(key, (cur) => (cur ? dropLocal(cur, id) : cur));
    try { await post("/api/plan/unsort", { itemIds: [id] }); }
    catch (e) { setError(e); }
    refresh();
  };

  const setMinutes = async (id: string, m: number) => {
    setError(null);
    qc.setQueryData<WeekData>(key, (cur) => (cur ? setMinutesLocal(cur, id, m) : cur));
    try { await patch(`/api/work/items/${id}`, { estimateMinutes: m }); }
    catch (e) { setError(e); }
    refresh();
  };

  const askSpread = async () => {
    if (!d) return;
    setSpreadBusy(true); setError(null);
    try { setSpread((await post<{ proposals: Proposal[] }>("/api/plan/week/spread", { week: d.week, apply: false })).proposals); }
    catch (e) { setError(e); }
    finally { setSpreadBusy(false); }
  };
  const applySpread = async () => {
    if (!d || !spread) return;
    setSpreadBusy(true); setError(null);
    try { await post("/api/plan/week/spread", { week: d.week, apply: true, include: spread.map((p) => p.itemId) }); setSpread(null); }
    catch (e) { setError(e); }
    finally { setSpreadBusy(false); refresh(); }
  };

  const goWeek = (week: string | undefined) => { setTab(null); void navigate({ to: "/plan/week", search: week ? { week } : {} }); };

  if (q.isLoading || !d) {
    return (
      <div className="space-y-5">
        <PlanSteps current="week" />
        <ErrorNote error={q.error} />
        <Empty>{q.isLoading ? "Looking at your week…" : "Couldn't load your week."}</Empty>
      </div>
    );
  }

  const thisMon = d.planWeek ?? mondayOf(d.today); // on a weekend: the coming week
  const left = d.unplaced.length;
  const loose = d.unplaced.length + d.dueThisWeek.length;
  const active = tab ?? (left ? LIST : d.days.find((x) => x.isToday)?.date ?? d.days.find((x) => !x.past)?.date ?? d.days[0]!.date);
  const fri = addDays(d.week, 4);
  const range = `${fmtDay(d.week, { day: "numeric", month: "short" })} – ${fmtDay(fri, { day: "numeric", month: "short", year: "numeric" })}`;

  const drop = (to: string | null) => (e: React.DragEvent) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData(DRAG_TYPE) || e.dataTransfer.getData("text/plain");
    if (id) void place(id, to);
  };
  const dragOver = (target: string, ok: boolean) => (e: React.DragEvent) => {
    if (!ok || !e.dataTransfer.types.includes(DRAG_TYPE)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (over !== target) setOver(target);
  };
  const dragLeave = (e: React.DragEvent) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(null); };

  const card = (t: WTask, where: string | null) => <TaskCard key={t.id} t={t} d={d} where={where} onMove={(to) => void (to === QUEUE ? toQueue(t.id) : place(t.id, to))} onMinutes={(m) => void setMinutes(t.id, m)} />;

  return (
    <div className="space-y-4">
      <PlanSteps current="week" left={left} />
      <PageHeader
        eyebrow="Plan · step 2"
        title="Plan my week"
        subtitle="Give every task this week a day. Days with meetings have less room."
        actions={
          <button className="btn btn-primary" disabled={spreadBusy || !loose || d.days.every((x) => x.past)} onClick={() => void askSpread()}>
            <Shuffle size={15} /> {spreadBusy && !spread ? "Thinking…" : "Spread for me"}
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <button className="btn btn-sm btn-icon" aria-label="Previous week" onClick={() => goWeek(addDays(d.week, -7))}><ChevronLeft size={16} /></button>
          <button className="btn btn-sm btn-icon" aria-label="Next week" onClick={() => goWeek(addDays(d.week, 7))}><ChevronRight size={16} /></button>
        </div>
        <div className="tabs" role="group" aria-label="Which week">
          <button className={clsx("tab", d.week === thisMon && "active")} aria-pressed={d.week === thisMon} onClick={() => goWeek(undefined)}>This week</button>
          <button className={clsx("tab", d.week === addDays(thisMon, 7) && "active")} aria-pressed={d.week === addDays(thisMon, 7)} onClick={() => goWeek(addDays(thisMon, 7))}>Next week</button>
        </div>
        <span className="text-sm font-semibold num">{range}</span>
      </div>

      {left > 0 ? (
        <div role="status" className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
          <AlertTriangle size={18} className="shrink-0" />
          <span className="text-base font-bold num">{left} still need{left === 1 ? "s" : ""} a day</span>
          <span className="text-sm">Drag each onto a day, use "Move to…", or let Spread for me propose days.</span>
        </div>
      ) : (
        <div role="status" className="flex flex-wrap items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-900">
          <Check size={18} className="shrink-0" />
          <span className="text-base font-bold">Week planned</span>
          {d.dueThisWeek.length > 0 && <span className="text-sm">{d.dueThisWeek.length} due this week still unsorted, see the list.</span>}
          <Link to="/plan" className="btn btn-sm ml-auto">Next: Plan my day <ChevronRight size={14} /></Link>
        </div>
      )}

      {!d.calendarConnected && (
        <p className="muted text-xs"><CalendarDays size={12} className="mr-1 inline" />Outlook isn't connected, so free time ignores meetings. Connect it in <Link to="/settings" className="underline">Settings</Link>.</p>
      )}
      <ErrorNote error={error} />

      {/* phone and tablet: one panel at a time */}
      <div className="max-w-full overflow-x-auto xl:hidden">
        <div className="tabs" role="tablist" aria-label="Day">
          <button role="tab" aria-selected={active === LIST} className="tab" onClick={() => setTab(LIST)}>To place{loose > 0 && <span className="count num">{loose}</span>}</button>
          {d.days.map((x) => (
            <button key={x.date} role="tab" aria-selected={active === x.date} className={clsx("tab", x.past && "opacity-60")} onClick={() => setTab(x.date)}>
              {fmtDay(x.date, { weekday: "short" })}{x.tasks.length > 0 && <span className="count num">{x.tasks.length}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="xl:grid xl:grid-cols-[15rem_minmax(0,1fr)] xl:items-start xl:gap-3">
        <section
          aria-label="To place this week"
          className={clsx("panel p-3 transition xl:sticky xl:top-2 xl:block", active !== LIST && "hidden", over === LIST && "ring-2 ring-brand-300")}
          onDragOver={dragOver(LIST, true)} onDragLeave={dragLeave} onDrop={drop(null)}
        >
          <h2 className="flex items-center gap-2 text-sm font-bold"><Inbox size={15} className="text-brand-600" /> To place this week <span className="muted ml-auto num text-xs">{left}</span></h2>
          <div className="mt-2 space-y-2">
            {d.unplaced.length ? d.unplaced.map((t) => card(t, null)) : <p className="muted py-3 text-center text-xs">Nothing waiting. Drop a card here to take it off its day.</p>}
          </div>
          {d.dueThisWeek.length > 0 && (
            <>
              <h3 className="mt-4 text-xs font-bold text-[var(--text-2)]">Due this week, not sorted <span className="muted num">{d.dueThisWeek.length}</span></h3>
              <div className="mt-2 space-y-2">{d.dueThisWeek.map((t) => card(t, null))}</div>
            </>
          )}
        </section>

        <div className="xl:grid xl:grid-cols-5 xl:gap-2">
          {d.days.map((x) => {
            const over_ = !x.past && x.taskMinutes > x.freeMinutes;
            const pct = x.freeMinutes ? Math.min(100, (x.taskMinutes / x.freeMinutes) * 100) : x.taskMinutes ? 100 : 0;
            return (
              <section
                key={x.date}
                aria-label={fmtDay(x.date, { weekday: "long", day: "numeric", month: "long" })}
                className={clsx("panel min-w-0 p-2.5 transition xl:block", active !== x.date && "hidden", x.past && "opacity-60", x.isToday && "ring-1 ring-brand-200", over === x.date && "ring-2 ring-brand-400 bg-brand-50/40")}
                onDragOver={dragOver(x.date, !x.past)} onDragLeave={dragLeave} onDrop={x.past ? undefined : drop(x.date)}
              >
                <header className="flex items-baseline gap-1.5">
                  <span className="text-sm font-bold">{fmtDay(x.date, { weekday: "short" })}</span>
                  <span className="muted text-xs num">{fmtDay(x.date, { day: "numeric", month: "short" })}</span>
                  {x.isToday && <span className="chip ml-auto !px-1.5 !text-[10px] text-brand-700">Today</span>}
                </header>
                <div className="mt-1.5" title={x.past ? "Past day" : `${dur(x.taskMinutes)} of tasks, ${dur(x.freeMinutes)} free`}>
                  <div className={clsx("text-[11px] num", over_ ? "font-semibold text-red-700" : "muted")}>
                    {x.past ? "Past" : `${dur(x.taskMinutes)} tasks / ${dur(x.freeMinutes)} free`}
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-black/[0.06]" role="meter" aria-label="Tasks against free time" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}>
                    <div className={clsx("h-full rounded-full", over_ ? "bg-red-500" : "bg-brand-500")} style={{ width: `${pct}%` }} />
                  </div>
                </div>
                {x.events.length > 0 && (
                  <ul className="mt-2 space-y-1" aria-label="Meetings">
                    {x.events.filter((e) => e.isAllDay).map((e) => <li key={e.id}><span className="chip max-w-full !text-[10px]" title={e.subject}><span className="truncate">All day: {e.subject}</span></span></li>)}
                    {x.events.filter((e) => !e.isAllDay).sort((a, b) => a.start.localeCompare(b.start)).map((e) => (
                      <li key={e.id} className="rounded-md bg-black/[0.05] px-1.5 py-1 text-[11px] leading-tight text-[var(--text-2)]" title={`${hm(e.start)}–${hm(e.end)} ${e.subject}${e.location ? ` · ${e.location}` : ""}`}>
                        <span className="num muted block">{hm(e.start)}–{hm(e.end)}</span>
                        <span className="block truncate">{e.subject}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-2 min-h-12 space-y-2">
                  {x.tasks.map((t) => card(t, x.date))}
                  {!x.tasks.length && !x.past && <p className="muted rounded-lg border border-dashed border-[var(--line)] py-3 text-center text-[11px]">Drop tasks here</p>}
                </div>
              </section>
            );
          })}
        </div>
      </div>

      {spread && (
        <Modal
          title="Spread for me"
          onClose={() => setSpread(null)}
          wide
          footer={<>
            <button className="btn" onClick={() => setSpread(null)}>Cancel</button>
            <button className="btn btn-primary" disabled={spreadBusy || !spread.length} onClick={() => void applySpread()}>{spreadBusy ? "Applying…" : `Apply ${spread.length}`}</button>
          </>}
        >
          {spread.length ? (
            <>
              <p className="muted text-sm">ONE would put each task on this day. Apply to accept them all; you can still drag them afterwards.</p>
              <ul className="divide-y divide-[var(--line)]">
                {spread.map((p) => (
                  <li key={p.itemId} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2 text-sm">
                    <span className="min-w-0 flex-1"><span className="muted num mr-1.5 text-xs">{p.ref}</span>{p.title}</span>
                    <span className="font-semibold">{fmtDay(p.day)}</span>
                    <span className="muted w-full text-xs">{p.why}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : <p className="muted text-sm">Nothing to spread: every task this week already has a day.</p>}
        </Modal>
      )}
    </div>
  );
}

function TaskCard({ t, d, where, onMove, onMinutes }: { t: WTask; d: WeekData; where: string | null; onMove: (to: string | null) => void; onMinutes: (m: number) => void }) {
  const [dragging, setDragging] = useState(false);
  const due = t.dueAt ? dueDay(t.dueAt) : null;
  const late = due && (due < d.today || (where && due < where));
  return (
    <article
      draggable
      onDragStart={(e) => { e.dataTransfer.setData(DRAG_TYPE, t.id); e.dataTransfer.setData("text/plain", t.id); e.dataTransfer.effectAllowed = "move"; setDragging(true); }}
      onDragEnd={() => setDragging(false)}
      className={clsx("group cursor-grab rounded-lg border border-[var(--line)] bg-[var(--panel)] p-2 text-xs shadow-sm active:cursor-grabbing", dragging && "opacity-40")}
    >
      <div className="flex items-start gap-1">
        <GripVertical size={13} className="muted mt-0.5 hidden shrink-0 xl:block" aria-hidden />
        <div className="min-w-0 flex-1">
          <Link to="/work/$id" params={{ id: t.id }} target="_blank" rel="noopener" draggable={false} className="line-clamp-2 text-[13px] font-medium leading-snug hover:underline">{t.title}</Link>
          <div className="muted mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <span className="num">{t.ref}</span>
            {t.company && <span className="chip !px-1.5 !text-[10px]">{t.company}</span>}
            {t.priority === "P1" && <span className="chip !px-1.5 !text-[10px] text-red-700">Urgent</span>}
            {t.decisionNeeded && <span className="chip !px-1.5 !text-[10px] text-amber-700">Decision</span>}
            {t.carriedFrom && <span className="chip !px-1.5 !text-[10px] text-sky-700" title={`Was planned for ${new Date(`${t.carriedFrom}T12:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} and not done`}>Carried over</span>}
            {t.dueAt && <span className={clsx(late && "font-semibold text-red-700")}>Due {fmtDue(t.dueAt)}</span>}
          </div>
        </div>
      </div>
      <div className="mt-1.5 flex items-center gap-1">
        <select
          className="input !h-6 !min-h-0 !w-auto !py-0 !pl-1.5 !pr-6 !text-[11px] !bg-[position:right_0.35rem_center]"
          value={t.minutes}
          onChange={(e) => onMinutes(Number(e.target.value))}
          aria-label={`Minutes for ${t.title}`}
          title={t.estimateMinutes == null ? "No estimate yet: counted as 30 minutes" : "How long it takes"}
        >
          {[...new Set([...MINUTES, t.minutes])].sort((a, b) => a - b).map((m) => <option key={m} value={m}>{dur(m)}{t.estimateMinutes == null && m === t.minutes ? "?" : ""}</option>)}
        </select>
        <select
          className="input !h-6 !min-h-0 !w-auto min-w-0 flex-1 !py-0 !pl-1.5 !pr-6 !text-[11px] !bg-[position:right_0.35rem_center]"
          value=""
          onChange={(e) => { const v = e.target.value; if (v) onMove(v === LIST ? null : v); }}
          aria-label={`Move ${t.title} to`}
        >
          <option value="">Move to…</option>
          {d.days.map((x) => <option key={x.date} value={x.date} disabled={x.past || x.date === where}>{fmtDay(x.date)}{x.isToday ? " (today)" : ""}</option>)}
          {where && <option value={LIST}>Back to this week's list</option>}
          <option value={QUEUE}>Back to the queue (To sort, no week)</option>
        </select>
      </div>
      <div className="mt-1"><CategoryPick itemId={t.id} value={t.category} /></div>
    </article>
  );
}
